import { faCheck } from '@fortawesome/pro-regular-svg-icons'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import IWDropdown, {
  DropdownValueProps,
} from 'shared/components/thunderbolt/IWDropdown'
import IWFormSection from 'shared/components/thunderbolt/IWFormSectionContainer'
import IWGenericCard, {
  IWGenericCardBody,
} from 'shared/components/thunderbolt/IWGenericCard'
import IWLoading from 'shared/components/thunderbolt/IWLoading'
import IWNoResultsMessage from 'shared/components/thunderbolt/IWNoResultsMessage'
import { useToast } from 'shared/components/thunderbolt/IWToastContext'
import { TIMEZONES } from 'shared/constants'
import { convertToCVS, downloadContent } from 'shared/helpers'
import { getPodList } from 'shared/podServiceClient'
import { WizardStepProps } from 'shared/types'
import styled from 'styled-components'
import { Pod } from '../../insightsManager/types'
import { convertBidsAndOffersCsvToJson, getLsData } from '../helpers'
import { lsErcotSubmissionPreviewTableColumns } from '../schemas'
import { LsErcotCsvRowItem } from '../types'
import FileUploadSection from './FileUploadSection'
import LSPodList from './LSPodList'

const StyledCardBody = styled(IWGenericCardBody)`
  padding-top: 1rem;
`

const CONSTANTS = {
  MARKET: 'ERCOT',
  TOAST_DURATION: 10_000,
  SUCCESS_TOAST_DURATION: 5_000,
  PODS_QUERY_LIMIT: 100,
} as const

type StepState = {
  dayAheadDemandBids: LsErcotCsvRowItem[]
}

type StepCurrentState = {
  bidAndOffers: LsErcotCsvRowItem[]
  nopPods: Pod[]
  stfPods: Pod[]
  availableNopOptions: DropdownValueProps[]
  selectedNopOption?: DropdownValueProps
  selectedNopPod?: Pod
  hasErrors: boolean
  isDownloading: boolean
}

export type Props = WizardStepProps<StepState>

const toastOptions = {
  canDismiss: true,
  duration: CONSTANTS.TOAST_DURATION,
}

const NetOpenPositionSection = ({
  options,
  selectedOption,
  onChange,
}: {
  options: DropdownValueProps[]
  selectedOption: DropdownValueProps | undefined
  onChange: (value: DropdownValueProps) => void
}) => {
  const { t } = useTranslation()
  return (
    <IWFormSection
      sectionTitle={t('loadScheduling.labels.selectNetOpenPosition')}
      sectionDescription={t(
        'loadScheduling.descriptions.selectNetOpenPosition',
      )}
    >
      <IWDropdown
        name="netOpenPosition"
        required
        fullWidth
        value={selectedOption}
        onChange={onChange}
        options={options}
        placeholder={t('placeholders.select')}
        hasError={!selectedOption}
      />
    </IWFormSection>
  )
}

export default function SelectStep({ onChange }: Props) {
  const { t } = useTranslation()
  const toast = useToast()

  const [currentState, setCurrentState] = useState<StepCurrentState>({
    bidAndOffers: [],
    hasErrors: true,
    nopPods: [],
    stfPods: [],
    availableNopOptions: [],
    isDownloading: false,
  })

  const { data: pods, isLoading: isLoadingPods } = useQuery(
    ['shortTermForecast-ls-bno-submit'],
    async () => {
      const listOfPods = await getPodList(0, CONSTANTS.PODS_QUERY_LIMIT, '', [
        'shortTermForecast',
        'riskManagement',
      ])

      return listOfPods.rows.filter((v) =>
        v.markets.some(
          (market) =>
            market.toLocaleLowerCase() === CONSTANTS.MARKET.toLocaleLowerCase(),
        ),
      )
    },
  )

  const onUpload = async (filename: string, file: File) => {
    try {
      const json = await convertBidsAndOffersCsvToJson(file)

      setCurrentState({
        ...currentState,
        hasErrors: false,
        bidAndOffers: json,
      })

      toast.grey(t('loadScheduling.toasts.uploadSuccessful'), {
        icon: faCheck,
        description: filename,
        canDismiss: true,
        duration: CONSTANTS.SUCCESS_TOAST_DURATION,
      })
    } catch (error) {
      console.error(error)
      toast.alert(t('errors.generic.processing'), toastOptions)
    }
  }

  const onDownload = async ({
    datasource = '',
    userPodId,
    userPodName,
    markets,
  }: Pod) => {
    try {
      setCurrentState((prev) => ({
        ...prev,
        bidAndOffers: [],
        isDownloading: true,
      }))

      const marketTimezone = TIMEZONES.ERCOT

      const bidsAndOffers = await getLsData(
        datasource,
        marketTimezone,
        markets,
        currentState.selectedNopPod,
      )

      const csvContent = convertToCVS(
        bidsAndOffers.updatedData,
        lsErcotSubmissionPreviewTableColumns.map(({ accessor }) => accessor),
      )

      if (!csvContent) {
        toast.warning(t('loadScheduling.convertWarningMessage'), toastOptions)

        setCurrentState((prev) => ({
          ...prev,
          isDownloading: false,
        }))

        return
      }

      downloadContent({
        filename: `${userPodName}-${userPodId}`,
        content: csvContent,
        ext: 'csv',
        mediaType: 'text/csv',
      })
    } catch (err) {
      console.error(err)

      setCurrentState((prev) => ({
        ...prev,
        hasErrors: true,
        bidAndOffers: [],
      }))

      toast.alert(t('errors.generic.processing'), toastOptions)
    } finally {
      setCurrentState((prev) => ({
        ...prev,
        isDownloading: false,
      }))
    }
  }

  const onSelect = async ({ datasource = '', markets }: Pod) => {
    try {
      if (!currentState?.selectedNopPod) {
        toast.warning(t('loadScheduling.toasts.pleaseSelectNOP'), toastOptions)
        return
      }

      setCurrentState((prev) => ({
        ...prev,
        bidAndOffers: [],
        isDownloading: true,
      }))

      const marketTimezone = TIMEZONES.ERCOT

      const data = await getLsData(
        datasource,
        marketTimezone,
        markets,
        currentState.selectedNopPod,
      )

      setCurrentState((prev) => ({
        ...prev,
        bidAndOffers: data.updatedData as LsErcotCsvRowItem[],
        hasErrors: false,
      }))
    } catch (e) {
      console.error(e)

      setCurrentState((prev) => ({
        ...prev,
        hasErrors: true,
        bidAndOffers: [],
      }))

      toast.alert(t('errors.generic.processing'), toastOptions)
    } finally {
      setCurrentState((prev) => ({
        ...prev,
        isDownloading: false,
      }))
    }
  }

  useEffect(() => {
    onChange(
      {
        dayAheadDemandBids: currentState.bidAndOffers,
      },
      !currentState.hasErrors && currentState.bidAndOffers.length > 0,
    )
  }, [currentState.bidAndOffers, currentState.hasErrors])

  useEffect(() => {
    if (!pods?.length) {
      return
    }

    const { nop, stf } = pods.reduce(
      (acc, pod) => {
        const { insightId, metricId } = pod.podType

        if (metricId === 'netOpenPosition') {
          acc.nop.push(pod)
        }

        if (insightId === 'shortTermForecast') {
          acc.stf.push(pod)
        }

        return acc
      },
      { nop: [], stf: [] } as { nop: Pod[]; stf: Pod[] },
    )

    const nopSelectionOptions = (nop || []).map((pod) => ({
      value: pod.userPodId,
      label: pod.userPodName,
    }))

    setCurrentState((prev) => {
      return {
        ...prev,
        nopPods: nop,
        stfPods: stf,
        availableNopOptions: nopSelectionOptions,
      }
    })
  }, [pods])

  useEffect(() => {
    if (!currentState?.selectedNopOption) {
      return
    }

    const pod = currentState.nopPods?.find(
      (p) => p.userPodId === currentState.selectedNopOption?.value,
    )

    setCurrentState((prev) => ({ ...prev, selectedNopPod: pod }))
  }, [currentState.selectedNopOption])

  return (
    <>
      <IWGenericCard>
        <StyledCardBody>
          <FileUploadSection
            onChange={(filename, file) => {
              onUpload(filename, file)
            }}
            onClear={() => {
              setCurrentState((prev) => ({
                ...prev,
                bidAndOffers: [],
              }))
            }}
          />
        </StyledCardBody>
      </IWGenericCard>

      {isLoadingPods && <IWLoading />}

      {!isLoadingPods && currentState.stfPods.length === 0 && (
        <IWNoResultsMessage />
      )}

      {currentState.availableNopOptions.length > 0 && (
        <IWGenericCard>
          <StyledCardBody>
            <NetOpenPositionSection
              options={currentState.availableNopOptions}
              selectedOption={currentState?.selectedNopOption}
              onChange={(selection: DropdownValueProps) => {
                setCurrentState((prev) => {
                  return {
                    ...prev,
                    selectedNopOption: selection,
                  }
                })
              }}
            />
          </StyledCardBody>
        </IWGenericCard>
      )}

      {!isLoadingPods && currentState.stfPods.length > 0 && (
        <IWGenericCard>
          <StyledCardBody>
            <IWFormSection
              sectionTitle={t('loadScheduling.titles.selectSTFForecast')}
              sectionDescription={t(
                'loadScheduling.descriptions.selectSTFForecast',
              )}
            >
              <LSPodList
                pods={currentState.stfPods}
                isLoading={currentState.isDownloading}
                onDownload={onDownload}
                onSelect={onSelect}
                selectedPodIds={[]}
                selectedNetOpenPosition={currentState.selectedNopPod}
              />
            </IWFormSection>
          </StyledCardBody>
        </IWGenericCard>
      )}
    </>
  )
}
